<template>
    <div class="image-lightbox-wrapper">
        <!-- Preview Image or Link -->
        <div v-if="!linkOnly" class="preview-container">
            <img
                 v-if="!previewError"
                 :src="imageUrl"
                 :height="previewHeight"
                 @click="showLightbox = true"
                 @error="handlePreviewError"
                 class="clickable-image"
                 :alt="alt" />
            <div v-else class="image-error">
                Bild nicht verfügbar
            </div>
        </div>

        <a
           v-else
           href="#"
           @click.prevent="!previewError && (showLightbox = true)"
           class="image-link"
           :class="{ 'disabled': previewError }"
           :title="alt">
            {{ alt }}
        </a>

        <!-- Lightbox Overlay -->
        <div v-if="showLightbox" class="lightbox-overlay" @click="closeLightbox">
            <div class="lightbox-content" @click.stop>
                <button class="lightbox-close" @click="closeLightbox">&times;</button>
                <div class="zoom-container" @mousemove="handleMouseMove" @mouseleave="resetZoom">
                    <img
                         v-if="!lightboxError"
                         :src="imageUrl"
                         class="lightbox-image"
                         :class="{ 'zoomed': isZoomed }"
                         :style="zoomStyle"
                         :alt="alt"
                         @error="handleLightboxError"
                         ref="lightboxImage" />
                    <div v-else class="lightbox-error">
                        Bild nicht verfügbar
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'ImageLightbox',
    props: {
        imageUrl: {
            type: String,
            required: true
        },
        previewHeight: {
            type: [String, Number],
            default: 250
        },
        alt: {
            type: String,
            default: 'Image preview'
        },
        linkOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            showLightbox: false,
            isZoomed: false,
            zoomFactor: 2,
            zoomX: 0,
            zoomY: 0,
            previewError: false,
            lightboxError: false
        }
    },
    computed: {
        zoomStyle() {
            if (!this.isZoomed) {
                return {
                    transform: 'scale(1)',
                    transformOrigin: 'center'
                }
            }
            return {
                transform: `scale(${this.zoomFactor})`,
                transformOrigin: `${this.zoomX}% ${this.zoomY}%`
            }
        }
    },
    methods: {
        closeLightbox() {
            this.showLightbox = false;
            this.resetZoom();
        },
        handleMouseMove(event) {
            const lightboxImage = this.$refs.lightboxImage;
            if (!lightboxImage) return;

            const rect = lightboxImage.getBoundingClientRect();
            const x = event.clientX - rect.left;
            const y = event.clientY - rect.top;

            // Check if mouse is within the image bounds
            if (x < 0 || y < 0 || x > rect.width || y > rect.height) {
                this.resetZoom();
                return;
            }

            // Calculate zoom origin as percentage of image dimensions
            const imageWidth = rect.width;
            const imageHeight = rect.height;

            this.zoomX = (x / imageWidth) * 100;
            this.zoomY = (y / imageHeight) * 100;

            this.isZoomed = true;
        },
        resetZoom() {
            this.isZoomed = false;
            this.zoomX = 0;
            this.zoomY = 0;
        },
        handlePreviewError() {
            this.previewError = true;
        },
        handleLightboxError() {
            this.lightboxError = true;
        }
    },
    watch: {
        imageUrl() {
            // Reset error states when image URL changes
            this.previewError = false;
            this.lightboxError = false;
        }
    }
}
</script>

<style lang="scss" scoped>
.image-lightbox-wrapper {
    display: inline-block;
}

.preview-container {
    display: inline-block;
}

.clickable-image {
    cursor: pointer;
    transition: opacity 0.2s ease;

    &:hover {
        opacity: 0.8;
    }
}

.image-error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 250px;
    width: 100%;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    color: #6c757d;
    font-size: 14px;
    text-align: center;
    padding: 20px;
    box-sizing: border-box;
    cursor: default;
}

.image-link {
    color: #007bff;
    text-decoration: none;
    cursor: pointer;
    transition: color 0.2s ease;

    &:hover {
        color: #0056b3;
        text-decoration: underline;
    }

    &.disabled {
        color: #6c757d;
        cursor: default;
        pointer-events: none;

        &:hover {
            color: #6c757d;
            text-decoration: none;
        }
    }
}

.lightbox-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
}

.lightbox-content {
    background-color: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
    position: relative;
    max-width: 90vw;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    overflow: hidden;
}

.lightbox-close {
    position: absolute;
    top: 10px;
    right: 10px;
    background-color: #f0f0f0;
    border: none;
    border-radius: 50%;
    width: 30px;
    height: 30px;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    font-size: 20px;
    color: #333;
    z-index: 10;

    &:hover {
        background-color: #e0e0e0;
    }
}

.lightbox-image {
    max-width: calc(90vw - 40px);
    max-height: calc(90vh - 40px);
    object-fit: contain;
    cursor: crosshair;
    transition: transform 0.2s ease;
    transform-origin: center;

    &.zoomed {
        transition: none;
    }
}

.lightbox-error {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 200px;
    width: 100%;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    color: #6c757d;
    font-size: 16px;
    text-align: center;
    padding: 40px;
    box-sizing: border-box;
}

.zoom-container {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
}
</style>
